import { useCallback, useRef } from 'react';
import { toast, type Id } from 'react-toastify';
import type { SaveRun } from '../services/quoteSaveLane';
import {
    RetiredSaveError,
    QuoteConflictError,
    PaymentAmountStaleError,
    UNSAVEABLE_DRAFT_MESSAGE,
} from '../services/quoteSaveErrors';
import { priceChangedBlock } from '../utils/pricingErrors';

const LOADING_COPY: Record<SaveRun['kind'], string> = {
    create: 'Creating quote...',
    update: 'Updating quote...',
};

const SUCCESS_COPY: Record<SaveRun['kind'], string> = {
    create: 'Quote created successfully!',
    update: 'Quote updated successfully!',
};

const ERROR_COPY = 'There was an error creating the quote!';

/** Loading, success and error toasts for each save run; a quiet run shows none. */
export const useSaveRunToasts = () => {
    const toastIds = useRef(new WeakMap<SaveRun, Id>());

    const onRunStart = useCallback((run: SaveRun) => {
        if (run.quiet) return;
        toastIds.current.set(
            run,
            toast.loading(LOADING_COPY[run.kind], {
                isLoading: true,
                autoClose: false,
                position: 'bottom-right',
            }),
        );
    }, []);

    const onRunSettled = useCallback((run: SaveRun, outcome: 'saved' | Error) => {
        const id = toastIds.current.get(run);
        if (id === undefined) return;
        toastIds.current.delete(run);
        if (outcome === 'saved') {
            toast.update(id, { render: SUCCESS_COPY[run.kind], type: 'success', isLoading: false, autoClose: 3000 });
            return;
        }
        if (
            outcome instanceof RetiredSaveError ||
            outcome instanceof QuoteConflictError ||
            outcome instanceof PaymentAmountStaleError ||
            priceChangedBlock(outcome) !== null
        ) {
            toast.dismiss(id);
            return;
        }
        toast.update(id, { render: ERROR_COPY, type: 'error', isLoading: false, autoClose: 3000 });
    }, []);

    return { onRunStart, onRunSettled };
};

export const UNSAVEABLE_DRAFT_TOAST_ID = 'quote-unsaveable-draft';

export const notifyUnsaveableDraft = (): void => {
    toast.error(UNSAVEABLE_DRAFT_MESSAGE, { toastId: UNSAVEABLE_DRAFT_TOAST_ID, position: 'bottom-right' });
};
